// The shot as it runs (paintShotLive() and the live curve of the local app): a point whenever the brew time advances
// during state 20 (not the rinse after a shot; liveCurveData() puts them on a 1-s grid), then the result stays a little
// after the shot, with the drops the machine counts after the stop. Pure; fed by the client.
import type { LiveDeltaMessage } from '@brewcore/app-contract';
import type { Live } from './status.js';

export interface LivePoint {
  /** brew time (s) */
  s: number;
  weight: number | null;
  flow: number | null;
  temp: number | null;
}

export interface LiveShotView {
  /** a shot runs right now */
  brewing: boolean;
  /** running or just finished (result shown for a while), null otherwise */
  shot: {
    points: readonly LivePoint[];
    time: number;
    weight: number | null;
    flow: number | null;
    /** pre-infusion phase while it runs: 0 none, 1 burst, 2 pause */
    pi: number;
    doneAt: number | null;
    held: boolean;
    /** the weight when the pump stopped (the last one of the shot), after it the drops add up in weight */
    stopWeight?: number | null;
    /** the scale was missing at some point of the shot: the firmware stops it by time then (brewWeightFallback) */
    noScale?: boolean;
    /** when the last value of the running shot arrived (this device's clock): the ring runs on from there */
    sampledAt?: number;
    /** after the stop: the drops still count (live "cup"), and its last value */
    counting?: boolean;
    lastCup?: number | null;
  } | null;
}

/**
 * A step of the drops' count larger than this is the cup lifted or something put down, not a drop: the count ends
 * there (firmware ShotLog kSettleJump, lib/Orione OrioneShots.h).
 */
export const CUP_JUMP_G = 5;

/**
 * The longest a finished shot is kept after the switch is back. The screen hides it earlier, after the machine's
 * display.post_brew_timer_duration (liveShotScreen, core/screens/machine.ts); rating stays on the card "Letzter Bezug".
 */
export const DONE_HOLD_MS = 120_000;

export class LiveShotTracker {
  private view: LiveShotView = { brewing: false, shot: null };
  private readonly listeners = new Set<() => void>();

  constructor(private readonly now: () => number = Date.now) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): LiveShotView => {
    const s = this.view.shot;
    if (s && !this.view.brewing && s.doneAt !== null && !s.held && this.now() - s.doneAt > DONE_HOLD_MS)
      this.view = { brewing: false, shot: null };
    return this.view;
  };

  private commit(v: LiveShotView): void {
    this.view = v;
    for (const fn of this.listeners) fn();
  }

  apply(m: LiveDeltaMessage): void {
    const d = m.data as Live & { cup?: number | null };
    const brewing = d.state === 20 && !d.rinse;
    const prev = this.view.shot;
    if (d.state === 20 && d.rinse) {
      // the rinse after a shot: its result goes at once and does not come back when the rinse waits for the switch
      // after its 10 s (the round display's cancel(), Dominik 22.10.2026: "geht weg und kommt wieder")
      if (prev) this.commit({ brewing: false, shot: null });
      return;
    }
    if (brewing) {
      const time = d.brewTime ?? 0;
      const fresh = !prev || prev.doneAt !== null || time < (prev.points.at(-1)?.s ?? 0) - 1;
      const points = fresh ? [] : [...prev.points];
      const last = points.at(-1);
      if (!last || time > last.s)
        points.push({
          s: time,
          weight: d.scale === 2 ? (d.weight ?? null) : null,
          flow: d.flow ?? null,
          temp: d.currentTemp ?? null,
        });
      this.commit({
        brewing: true,
        shot: {
          points,
          time,
          weight: d.scale === 2 ? (d.weight ?? null) : null,
          flow: d.flow ?? null,
          pi: d.pi ?? 0,
          doneAt: null,
          held: false,
          noScale: (!fresh && prev?.noScale === true) || d.scale !== 2,
          sampledAt: this.now(),
        },
      });
      return;
    }
    // After the stop the weight is what the machine counts in the cup since the start ("cup", the drops included;
    // ShotLog.settle()), never the scale's reading: with the cup lifted that is below zero (Dominik 22.10.2026: a
    // wrong total at the end). The highest count stays; a jump ends the count, as does the switch going back
    // (Dominik 21.10.2026: "wenn der S3 umgelegt wird, soll die Waage nicht mehr weiterzählen").
    const cup = d.scale === 2 && typeof d.cup === 'number' ? d.cup : null;
    if (prev && prev.doneAt === null) {
      // the first value after the shot: its final time, and the weight at the stop with the first drops
      const base = prev.weight ?? 0;
      const take = cup !== null && prev.weight !== null && cup - base <= CUP_JUMP_G;
      this.commit({
        brewing: false,
        shot: {
          ...prev,
          time: Math.max(prev.time, d.brewTime ?? 0),
          weight: take ? Math.max(base, cup) : prev.weight,
          stopWeight: prev.weight,
          pi: 0,
          doneAt: this.now(),
          held: d.held === true,
          counting: take && d.held === true,
          lastCup: take ? cup : null,
        },
      });
      return;
    }
    if (!prev) return;
    const still =
      prev.counting === true &&
      d.held === true &&
      cup !== null &&
      Math.abs(cup - (prev.lastCup ?? cup)) <= CUP_JUMP_G;
    const weight = still && cup > (prev.weight ?? 0) ? cup : prev.weight;
    // the switch held the shot from its end (the first value after it) or never: a later "held" belongs to another
    // run of the switch (the rinse after its 10 s), never to this shot
    const held = prev.held && d.held === true;
    if (prev.held !== held || prev.counting !== still || weight !== prev.weight)
      this.commit({
        brewing: false,
        shot: {
          ...prev,
          held,
          weight,
          counting: still,
          lastCup: still ? cup : prev.lastCup,
          doneAt: prev.held && !held ? this.now() : prev.doneAt,
        },
      });
  }
}
